import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { ProgressIndicator as ProgressBar } from '@vodacomsa/ns-react-components';
import './DocumentUpload.css';
import {
  DocumentCard,
  ErrorModalSection
} from '../../components';
import { formatBytes } from '../../helpers';
import { listDocuments } from '../../api/vodaLend';
import {
  axiosInstance
} from '../../api/vodaLend/axiosInstance';
import { useLoader } from '../../components/Loader/useLoader';
import {
  useRecoilValue,
  useSetRecoilState,
  useResetRecoilState
} from 'recoil';
import { useModal } from '../../components/modalController/useModal';
import {
  documentData,
  documentsByType,
  useShowToast,
  documentsListState
} from '../../recoil';
import { useFindErrorOnState } from '../../hooks/useFindErrorOnState';
import { v4 as uuidv4 } from 'uuid';
import { useTealium } from '../../tealium/useTealium';
import { StatusCodes } from 'http-status-codes';

const DOCTYPE_STATEMENT = 'statement';
const VALID_UPLOAD_BYTES_SIZE = 4.5 * (1000 * 1000);

export const BankStatementUpload = ({
  setIsDocumentUploaded
}) => {
  const [openModal, closeModal] = useModal();
  const statementFileIdRef = useRef(uuidv4());
  const statementFile = useRecoilValue(documentData(statementFileIdRef.current));
  const [fileName, setFileName] = useState(statementFile?.name || '');
  const [isAllowSize, setIsAllowSize] = useState(false);
  const [isAllowType, setIsAllowType] = useState(false);
  const [multipleDocs, setMultipleDocs] = useState(false);
  const listDocumentRecord = useRecoilValue(documentsListState);
  const resetDocList = useResetRecoilState(documentsListState);
  const { track, events } = useTealium();
  const showToast = useShowToast();
  const [openLoader, closeLoader] = useLoader();
  const loadDocumentData = useSetRecoilState(documentData(statementFileIdRef?.current));
  const resetStatementFiile = useResetRecoilState(documentData(statementFileIdRef.current));
  const [docList, setDocList] = useState(listDocumentRecord || []);
  const [uploadPercentage, setUploadPercentage] = useState(0);

  useEffect(() => {
    track(events.uploadDocuments.pageView());
  }, []);

  useFindErrorOnState([documentsByType(DOCTYPE_STATEMENT), documentData(statementFileIdRef.current)], () => {
    resetDocList();
    resetStatementFiile();
  });

  const shouldShowOptionToUpload = (percentComplete, docList) => {
    let shouldShowOption = true;
    if (percentComplete || docList?.length > 0) {
      shouldShowOption = false;
    }
    return shouldShowOption;
  };

  const getFilenameWithoutExtension = (filename) => {
    return filename.replace(/\.[^/.]+$/, '');
  };

  const closeErrorModal = () => {
    closeModal();
    setUploadPercentage(0);
  };

  const handleUpload = async (uniqueFilename, type, statementFile) => {
    if (!statementFile) { return; }
    openLoader();
    let response;
    const businessId = sessionStorage.getItem('businessId');
    const docName = getFilenameWithoutExtension(uniqueFilename);
    const body = new FormData();
    const iconType = 'info';
    const timeout = 'long';
    body.append(uniqueFilename, statementFile, uniqueFilename);
    body.append('docType', type);
    body.append('docName', docName);
    try {
      response = await axiosInstance.post(`/business-documents/${businessId}/document`, body, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          closeLoader();
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadPercentage(percent);
        }
      });
      if(response.status === StatusCodes.CREATED) {
        showToast(
          'Bank statement upload successful.',
          iconType,
          timeout,
          '',
          true
        );
        openLoader();
        setIsDocumentUploaded(true);
        const getResponse = await listDocuments();
        setDocList(getResponse);
        closeLoader();
      }
    } catch (error) {
      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={response?.data?.traceId}
          error={response?.data?.error}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });
    }
  };

  const uploadStatement = async (statementFile) => {
    if (!(statementFile.size <= VALID_UPLOAD_BYTES_SIZE)) {
      setIsAllowSize(true);
      closeLoader();
      return;
    }
    if (!(statementFile.type === 'application/pdf')) {
      setIsAllowType(true);
      closeLoader();
      return;
    }

    const fileName = statementFile.name;
    setFileName(fileName);
    closeLoader();
    handleUpload(fileName, DOCTYPE_STATEMENT, statementFile);
    track(events.bankingDetails.attachFile());
    statementFileIdRef.current = uuidv4();
  };

  useEffect(() => {
    if (listDocumentRecord.error) {
      openModal({
        title: 'Error',
        children: <ErrorModalSection
          traceId={listDocumentRecord?.traceId}
          error={listDocumentRecord?.error}
          onCloseErrorModal={closeErrorModal}
        />,
        type: 'error',
        onCloseModal: closeErrorModal
      });
    }
    setDocList(listDocumentRecord);
  }, [listDocumentRecord]);

  useEffect(() => {
    if (statementFile) {
      setMultipleDocs(false);
      setIsAllowSize(false);
      setIsAllowType(false);
      if (statementFile.length === 1) {
        statementFile.forEach(file => {
          uploadStatement(file);
        });
      } else {
        setMultipleDocs(true);
        closeLoader();
      }
    }
  }, [statementFile]);

  const preventDefault = (e) => {
    openLoader();
    e.preventDefault();
    e.stopPropagation();
  };

  const DocumentUploadCompletionComp = () => (
    <>
      <div className='progressContainer' >
        <h2 className="heading">Upload bank statements</h2>
        <div className="row">
          <p className="instructions">
            To speed up the application processes, please upload a copy of your latest bank statement not older than 12 months or Proof of Banking letter.
          </p>
        </div>
        <span className="supportedText">Supported file type:</span><span className='supportedTypeText' >PDF</span>
      </div>
    </>
  );

  const initiateDocumentUploadComp = () => (
    <>
      <div>
        <h2 className="heading">Upload bank statements</h2>
        <div className="row">
          <p className="instructions">
            To speed up the application processes, please upload a copy of your latest bank statement not older than 12 months or Proof of Banking letter.
          </p>
          <label htmlFor="file-upload" className="uploadButton">
            📎 Attach file
            <input id="file-upload" type="file" accept=".pdf" className="hiddenInput"
              onChange={(e) => {
                setIsAllowSize(false);
                setIsAllowType(false);
                preventDefault(e);
                const files = e.target.files;
                const fileArray = Array.from(files);
                loadDocumentData(fileArray);
                e.target.value = '';
              }}
            />
          </label>
        </div>
        <span className="supportedText">Supported file type:</span><span className='supportedTypeText' >PDF</span>
      </div>
    </>
  );
  return (
    <div className="container">
      {
        shouldShowOptionToUpload(uploadPercentage, docList?.documents) &&
        initiateDocumentUploadComp()
      }
      {
        !shouldShowOptionToUpload(uploadPercentage, docList?.documents) &&
        DocumentUploadCompletionComp()
      }
      {
        uploadPercentage > 0 && uploadPercentage <= 100 && docList?.documents?.length === 0 &&
        (<div><span><ProgressBar className='progressBarContainer' value={uploadPercentage} filledClass='blueColor' /></span><span className='progressCounter' >{uploadPercentage}%</span><p className='bottomLabelProgressBar '>{fileName}...</p> </div>)
      }
      {
        <div>
          {docList?.documents?.map((f) => (<DocumentCard key={f.docId} docName={f.docName} docId={f.docId} docSize={f.docSize} setIsDocumentUploaded={setIsDocumentUploaded} setUploadPercentage={setUploadPercentage} />))}
        </div>
      }
      {isAllowSize &&
          <div className='errorMessage'>*Bank Statement Upload: Document must not exceed size of {formatBytes(VALID_UPLOAD_BYTES_SIZE)}.</div>
      }
      {isAllowType &&
          <div className='errorMessage'>*Bank Statement Upload: Document must be in PDF format.</div>
      }
      {multipleDocs &&
        <div className='errorMessage'>*Multiple files Upload is not allowed.</div>
      }
    </div>
  );
};

BankStatementUpload.propTypes = {
  setIsDocumentUploaded: PropTypes.func
};
